// adminportal/src/pages/SftpConnectors.tsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Server, Building2 } from 'lucide-react';
import { apiUrl } from '../config';
import SftpConnectorManager from '../components/SftpConnectorManager';

interface SftpConnector {
  connectorId: string;
  name: string;
  host: string;
  port: number;
  username: string;
  companyId: string;
  companyName?: string;
  workflowType: 'INBOUND' | 'OUTBOUND';
  destinationType: 'COMPANY_USERS' | 'PRIMARY_COMPANY' | 'CUSTOM_PATH';
  s3Bucket: string;
  s3Prefix: string;
  authenticationType: 'password' | 'ssh-key';
  tags?: Record<string, string>;
  createdAt: string;
  updatedAt: string;
}

export default function SftpConnectors() {
  const [connectors, setConnectors] = useState<SftpConnector[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<string | null>(null);

  useEffect(() => {
    // SftpConnectors page loaded
    loadAllConnectors();
  }, []);

  const loadAllConnectors = async () => {
    setLoading(true);
    setError(null);
    try {
      // Load all connectors without company filter
      const apiEndpoint = apiUrl('sftp-connectors');
      // Loading all connectors from API

      const response = await fetch(apiEndpoint, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      // Response status received
      // Response headers received

      if (!response.ok) {
        await response.text();
        // API Error Response
        throw new Error(`Failed to load connectors: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      // Connectors data received
      // Number of connectors loaded

      if (data.connectors && Array.isArray(data.connectors)) {
        setConnectors(data.connectors);
      } else {
        // Unexpected data format
        setConnectors([]);
      }
    } catch (err: unknown) {
      // Error loading connectors
      setError(err instanceof Error ? err.message : 'An error occurred');
      setConnectors([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className='p-4 max-w-6xl mx-auto'>
        <div className='text-center py-8 text-slate-400'>
          Loading SFTP connectors...
          <div className='text-xs text-slate-500 mt-2'>Check console for debug info</div>
        </div>
      </div>
    );
  }

  return (
    <div className='p-4 max-w-6xl mx-auto'>
      <div className='flex items-center justify-between mb-6'>
        <h1 className='text-2xl font-semibold text-slate-100 flex items-center gap-2'>
          <Server className='w-6 h-6' />
          SFTP Connectors
        </h1>
        <Link to='/admin-dashboard' className='text-blue-400 hover:text-blue-300 text-sm'>
          ← Back to Dashboard
        </Link>
      </div>

      {error && (
        <div className='mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg'>
          <div className='flex items-center justify-between'>
            <div>
              <p className='text-red-400 font-medium'>Error loading connectors</p>
              <p className='text-red-300 text-sm mt-1'>{error}</p>
            </div>
            <button
              onClick={loadAllConnectors}
              className='px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-sm rounded transition-colors'
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Company Filter */}
      <div className='mb-6'>
        <div className='flex items-center gap-4'>
          <label className='text-sm font-medium text-slate-300'>Filter by Company:</label>
          <select
            value={selectedCompany || ''}
            onChange={e => setSelectedCompany(e.target.value || null)}
            className='px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white'
            aria-label='Select company to filter connectors'
          >
            <option value=''>All Companies</option>
            {Array.from(new Set(connectors.map(c => c.companyId))).map(companyId => {
              const connector = connectors.find(c => c.companyId === companyId);
              return (
                <option key={companyId} value={companyId}>
                  {connector?.companyName || companyId}
                </option>
              );
            })}
          </select>
        </div>
      </div>

      {/* Show filtered connectors or company-specific view */}
      {selectedCompany ? (
        <div>
          <div className='mb-4'>
            <button
              onClick={() => setSelectedCompany(null)}
              className='text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1'
            >
              ← Back to All Connectors
            </button>
          </div>
          <SftpConnectorManager
            companyId={selectedCompany}
            companyName={
              connectors.find(c => c.companyId === selectedCompany)?.companyName || selectedCompany
            }
          />
        </div>
      ) : (
        <div>
          {connectors.length === 0 ? (
            <div className='text-center py-12'>
              <Server className='w-16 h-16 mx-auto mb-4 text-slate-400' />
              <h3 className='text-lg font-medium text-slate-200 mb-2'>No SFTP Connectors</h3>
              <p className='text-slate-400 mb-6'>
                Create SFTP connectors through company management to enable file transfers
              </p>
              <Link
                to='/companies'
                className='inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors'
              >
                <Building2 className='w-4 h-4' />
                Go to Companies
              </Link>
            </div>
          ) : (
            <div className='grid gap-4'>
              {connectors
                .filter(connector =>
                  selectedCompany ? connector.companyId === selectedCompany : true
                )
                .map(connector => (
                  <div
                    key={connector.connectorId}
                    className='bg-slate-800 border border-white/10 rounded-lg p-4'
                  >
                    <div className='flex items-center justify-between'>
                      <div className='flex-1'>
                        <div className='flex items-center gap-3 mb-2'>
                          <h3 className='font-medium text-white'>{connector.name}</h3>
                          <span className='px-2 py-1 text-xs bg-slate-700 text-slate-300 rounded'>
                            {connector.authenticationType}
                          </span>
                          <div className='flex items-center gap-2'>
                            <span className='px-2 py-1 text-xs bg-green-600/20 text-green-400 rounded'>
                              {connector.workflowType}
                            </span>
                            <span className='px-2 py-1 text-xs bg-blue-600/20 text-blue-400 rounded'>
                              {connector.companyName || connector.companyId}
                            </span>
                          </div>
                        </div>
                        <div className='text-sm text-slate-400 space-y-1'>
                          <p>
                            Host: {connector.host}:{connector.port}
                          </p>
                          <p>Username: {connector.username}</p>
                          <p>
                            Destination: {connector.s3Bucket}/{connector.s3Prefix}
                          </p>
                          <p>Company: {connector.companyName || connector.companyId}</p>
                          <p>Created: {new Date(connector.createdAt).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <div className='flex items-center gap-2'>
                        <button
                          onClick={() => setSelectedCompany(connector.companyId)}
                          className='px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded transition-colors'
                        >
                          Manage
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
